import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesBusinessTravelListQueryKey,
  getCompanyReportsActivitiesBusinessTravelRetrieveQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesBusinessTravelFuelUsageCreate,
  useCompanyReportsActivitiesBusinessTravelFuelUsageUpdate,
  useCompanyReportsActivitiesBusinessTravelList,
  useCompanyReportsActivitiesBusinessTravelPassengerTransportCreate,
  useCompanyReportsActivitiesBusinessTravelPassengerTransportUpdate,
  useCompanyReportsActivitiesBusinessTravelRetrieve,
  useCompanyReportsActivitiesBusinessTravelTotalEmissionsCreate,
  useCompanyReportsActivitiesBusinessTravelTotalEmissionsUpdate,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsPassengerTransportModesList,
} from '@shared/api/lib/company-reports/company-reports';
import {
  BusinessTravelActivityRetrieve,
  CompanyReportsActivitiesBusinessTravelListParams,
  CompanyReportsPassengerTransportModesListParams,
  PaginatedBusinessTravelActivitiesListList,
} from '@shared/api/types';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

const invalidateAfterMutation = (
  queryClient: QueryClient,
  companyReportUuid: string,
  activityUuid?: string
) => {
  void refreshQueries(
    queryClient,
    getCompanyReportsRetrieveQueryKey(companyReportUuid)
  );
  return Promise.all([
    refreshQueries(
      queryClient,
      getCompanyReportsActivitiesBusinessTravelListQueryKey(companyReportUuid)
    ),
    activityUuid &&
      refreshQueries(
        queryClient,
        getCompanyReportsActivitiesBusinessTravelRetrieveQueryKey(
          companyReportUuid,
          activityUuid
        )
      ),
  ]);
};

export const useGetBusinessTravelActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesBusinessTravelListParams
) =>
  useCompanyReportsActivitiesBusinessTravelList<PaginatedBusinessTravelActivitiesListList>(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 24,
      },
    }
  );

export const useGetBusinessTravelActivity = ({
  reportUuid,
  activityUuid,
}: {
  reportUuid: string;
  activityUuid?: string;
}) =>
  useCompanyReportsActivitiesBusinessTravelRetrieve<BusinessTravelActivityRetrieve>(
    reportUuid,
    activityUuid!,
    {
      request: useAxiosConfig(),
      query: {
        enabled: !!activityUuid,
      },
    }
  );

export const useCreateFuelUsageBusinessTravelActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesBusinessTravelFuelUsageCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) =>
        invalidateAfterMutation(queryClient, variables.companyReportUuid),
    },
  });
};

export const useUpdateFuelUsageBusinessTravelActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesBusinessTravelFuelUsageUpdate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) =>
        invalidateAfterMutation(
          queryClient,
          variables.companyReportUuid,
          variables.activityUuid
        ),
    },
  });
};

export const useCreatePassengerTransportBusinessTravelActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesBusinessTravelPassengerTransportCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) =>
        invalidateAfterMutation(queryClient, variables.companyReportUuid),
    },
  });
};

export const useUpdatePassengerTransportBusinessTravelActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesBusinessTravelPassengerTransportUpdate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) =>
        invalidateAfterMutation(
          queryClient,
          variables.companyReportUuid,
          variables.activityUuid
        ),
    },
  });
};

export const useCreateTotalEmissionsBusinessTravelActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesBusinessTravelTotalEmissionsCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) =>
        invalidateAfterMutation(queryClient, variables.companyReportUuid),
    },
  });
};

export const useUpdateTotalEmissionsBusinessTravelActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesBusinessTravelTotalEmissionsUpdate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) =>
        invalidateAfterMutation(
          queryClient,
          variables.companyReportUuid,
          variables.activityUuid
        ),
    },
  });
};

export const useDeleteBusinessTravelActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) =>
        invalidateAfterMutation(
          queryClient,
          variables.companyReportUuid,
          variables.activityUuid
        ),
    },
  });
};

export const useListPassengerTransportModes = (
  params?: CompanyReportsPassengerTransportModesListParams,
  queryOptions?: { enabled?: boolean }
) =>
  useCompanyReportsPassengerTransportModesList(params, {
    request: useAxiosConfig(),
    query: {
      staleTime: Infinity,
      ...queryOptions,
    },
  });
